import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import FormatCurrencyLocal from 'eco-design-reliance/currency/FormatCurrencyLocal';
import { useRouter } from 'next/router';
import { saveCookieForShopify } from '@elem/ui-common/utils/CookieForShopify';
import { BundleItemType } from '../data';
import css from './index.module.scss';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import { useState } from 'react';

export default function ProductItem({
    className = '',
    item,
    isSingle = false,
    callback,
}: {
    item: BundleItemType;
    className?: string;
    isSingle?: boolean;
    callback: (parentVariantId: string, variantId: string, type: 'plus' | 'minus') => void;
}) {
    const { t } = useLang('dpux.bundle');
    const { locale = '' } = useRouter();
    const [MIN, MAX] = [0, 100];
    const {
        variantId = '',
        variantImg = '',
        img = '',
        productTitle = '',
        skuTitle = '',
        power = '',
        desc = '',
        subs,
        price,
        compareAtPrice,
        inventoryQuantity,
        inventoryPolicy,
        tip = '',
        link_buy = '',
        btn = '',
    } = item;

    const getDynamicLinkBuy = (item: BundleItemType): string => {
        let link = `https://${locale}.ecoflow.com/cart/${item.variantId}:1`;
        if (item?.subs && item?.subs?.length > 0) {
            const hasQuantiySubs = item?.subs?.filter((sub: BundleItemType) => typeof sub?.quantity === 'number' && sub?.quantity > 0);
            hasQuantiySubs?.map((sub: BundleItemType) => {
                link += `,${sub?.variantId}:${sub?.quantity}`;
            });
        }
        link += `?channel=buy_button`;
        return link;
    };

    const handleCalc = (parentVariantId: string, variantId: string, type: 'plus' | 'minus', sub: BundleItemType, index: number) => {
        callback(parentVariantId, variantId, type);
        setTottalPrice((data) => {
            if (type === 'plus') {
                return {
                    price: data.price + sub.price,
                    compareAtPrice: data.compareAtPrice + (sub.compareAtPrice ?? sub.price),
                };
            } else {
                return {
                    price: data.price - sub.price,
                    compareAtPrice: data.compareAtPrice - (sub.compareAtPrice ?? sub.price),
                };
            }
        });
        gtm.push({
            event: 'uaEvent',
            event_name: 'bundle_add_to_cart',
            list_name: 'Choose a Solution',
            product_name: `${skuTitle || productTitle}_${sub.skuTitle || sub.productTitle}`,
            price: sub.price || 0,
            tag: 'none',
            quantity: 1,
            button_name: type === 'plus' ? '+' : '-',

            __btn_id1: `sub-calc .${type}-${index}`,
            __btn_id2: `${type}-${index}`,
            __btn_type: 'common',
        });
    };

    const [tottalPrice, setTottalPrice] = useState({
        price: 0,
        compareAtPrice: 0,
    });
    // 向下取整
    const tagNumber = (compareAtPrice ?? price) + tottalPrice.compareAtPrice - (price + tottalPrice.price);
    const renderBtns = () => (
        <div className="item-cont">
            <div className="item-price">
                <div className="item-price-box">
                    {typeof price === 'number' && price >= 0 && (
                        <span className="item-price-price">{FormatCurrencyLocal(price + tottalPrice.price, locale)}</span>
                    )}
                    {compareAtPrice && typeof compareAtPrice === 'number' && compareAtPrice >= 0 && compareAtPrice !== price && (
                        <span className="item-price-compare">{FormatCurrencyLocal(compareAtPrice + tottalPrice.compareAtPrice, locale)}</span>
                    )}
                    {tagNumber > 0 && <span className="item-price-tag">{FormatCurrencyLocal(tagNumber, locale)} OFF</span>}
                </div>
                {tip && <p className="item-tip">{tip}</p>}
            </div>
            {inventoryPolicy === 'continue' || (typeof inventoryQuantity === 'number' && inventoryQuantity > 0) ? (
                <EcoLink
                    href={saveCookieForShopify(link_buy || getDynamicLinkBuy(item), locale)}
                    className={`item-btn GtmModuleData-btn-common item-btn-${variantId}`}
                    target="_blank"
                    data-btn-id={variantId}
                    dataLayer={{
                        event: 'uaEvent',
                        ...((link_buy || getDynamicLinkBuy(item)).includes(`https://${locale}.ecoflow.com/cart`)
                            ? {
                                  event_name: 'checkout',
                                  button_name: `${btn || t('item_buy')}_${skuTitle || productTitle}`,
                                  display_type: 'none',
                                  module_referral: 'none',
                                  jump_module: 'none',
                              }
                            : {
                                  event_name: 'buy',
                                  product_name: 'EcoFlow DELTA Pro Ultra X',
                                  position: 'Product Preview_Bundles',
                                  display_type: 'none',
                                  module_referral: 'none',
                                  jump_module: 'none',
                              }),

                        __btn_id1: `box-inner .item-btn-${variantId}`,
                        __btn_id2: variantId,
                        __btn_type: 'common',
                    }}
                >
                    {btn || t('item_buy')}
                </EcoLink>
            ) : (
                <>
                    {(variantId || btn) && (
                        <button disabled className="item-btn disabled GtmModuleData-btn-text">
                            {variantId ? t('item_sold_out') : btn}
                        </button>
                    )}
                </>
            )}
        </div>
    );

    const getTitle = (skuTitle?: string, productTitle?: string) => {
        if (skuTitle && skuTitle !== 'Default Title') {
            return skuTitle;
        } else if (productTitle) {
            return productTitle;
        }
    };

    return (
        <div className={`${css['bundle-item']} bundle-item ${className} ${isSingle ? css['single'] : ''}`}>
            <div className="item-info">
                <div className="item-img-box">
                    <EcoImg className="item-img" pc={variantImg || img} />
                </div>
                <div className="box-inner">
                    <div className="item-inner-info">
                        <p className="item-title">{getTitle(skuTitle, productTitle)}</p>
                        {power && <p className="item-power">{power}</p>}
                        {desc && <p className="item-desc">{desc}</p>}
                        {subs && subs?.length > 0 && (
                            <>
                                <p className="item-subs-label">{t('subs_label')}</p>
                                <ul className="item-subs">
                                    {subs?.map((sub: BundleItemType, index: number) => (
                                        <li key={sub?.variantId} className="item-sub">
                                            <div className="sub-img">
                                                <EcoImg key={`sub-${sub?.parentVariantId}-${sub?.variantId}`} pc={sub?.variantImg || sub?.img} />
                                            </div>
                                            <div className="sub-info">
                                                <p className="sub-title">{getTitle(sub?.skuTitle, sub?.productTitle)}</p>
                                                <div className="sub-cont">
                                                    <div className="sub-price">
                                                        {typeof sub?.price === 'number' && sub?.price >= 0 && (
                                                            <span className="sub-price-price">{FormatCurrencyLocal(sub?.price, locale)}</span>
                                                        )}
                                                        {sub?.compareAtPrice &&
                                                            typeof sub?.compareAtPrice === 'number' &&
                                                            sub?.compareAtPrice >= 0 &&
                                                            sub?.compareAtPrice !== sub?.price && (
                                                                <span className="sub-price-compare">{FormatCurrencyLocal(sub?.compareAtPrice, locale)}</span>
                                                            )}
                                                    </div>
                                                    <div className="sub-calc">
                                                        <svg
                                                            className={`minus GtmModuleData-btn-common minus-${index} calc-icon${
                                                                Number(sub?.quantity) <= MIN ? ' disabled' : ''
                                                            } GtmModuleData-btn-text`}
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="21"
                                                            height="20"
                                                            viewBox="0 0 21 20"
                                                            fill="none"
                                                            data-btn-id={`minus-${index}`}
                                                            onClick={() =>
                                                                Number(sub?.quantity) <= MIN
                                                                    ? undefined
                                                                    : handleCalc(sub?.parentVariantId || '', sub?.variantId || '', 'minus', sub, index)
                                                            }
                                                        >
                                                            <rect x="0.5" width="20" height="20" rx="10" fill="white" />
                                                            <path
                                                                fillRule="evenodd"
                                                                clipRule="evenodd"
                                                                d="M7.37016 10.454H13.6288C13.7767 10.454 13.8981 10.4114 13.9932 10.3262C14.0883 10.2409 14.1358 10.132 14.1358 9.99947C14.1358 9.86689 14.0883 9.75799 13.9932 9.67276C13.8981 9.58754 13.7767 9.54492 13.6288 9.54492H7.37016C7.22227 9.54492 7.10078 9.58754 7.00571 9.67276C6.91063 9.75799 6.8631 9.86689 6.8631 9.99947C6.8631 10.132 6.91063 10.2409 7.00571 10.3262C7.10078 10.4114 7.22227 10.454 7.37016 10.454Z"
                                                                fill="black"
                                                            />
                                                        </svg>
                                                        <span className="calc-text">{sub.quantity}</span>
                                                        <svg
                                                            className={`plus GtmModuleData-btn-common plus-${index} calc-icon${
                                                                Number(sub?.quantity) >=
                                                                (sub?.inventoryPolicy === 'continue' ? MAX : Math.min(sub?.inventoryQuantity || 0, MAX))
                                                                    ? ' disabled'
                                                                    : ''
                                                            } GtmModuleData-btn-text`}
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="21"
                                                            height="20"
                                                            viewBox="0 0 21 20"
                                                            fill="none"
                                                            data-btn-id={`plus-${index}`}
                                                            onClick={() =>
                                                                Number(sub?.quantity) >=
                                                                (sub?.inventoryPolicy === 'continue' ? MAX : Math.min(sub?.inventoryQuantity || 0, MAX))
                                                                    ? undefined
                                                                    : handleCalc(sub?.parentVariantId || '', sub?.variantId || '', 'plus', sub, index)
                                                            }
                                                        >
                                                            <rect x="0.5" width="20" height="20" rx="10" fill="white" />
                                                            <path
                                                                fillRule="evenodd"
                                                                clipRule="evenodd"
                                                                d="M7.36992 10.5068H9.99216V13.129C9.99216 13.2769 10.0397 13.3984 10.1348 13.4935C10.2298 13.5886 10.3513 13.6361 10.4992 13.6361C10.6471 13.6361 10.7686 13.5886 10.8637 13.4935C10.9587 13.3984 11.0063 13.2769 11.0063 13.129V10.5068H13.6285C13.7764 10.5068 13.8979 10.4593 13.993 10.3642C14.088 10.2691 14.1356 10.1476 14.1356 9.99974C14.1356 9.85184 14.088 9.73036 13.993 9.63529C13.8979 9.54021 13.7764 9.49267 13.6285 9.49267H11.0063V6.87044C11.0063 6.72254 10.9587 6.60106 10.8637 6.50599C10.7686 6.41091 10.6471 6.36337 10.4992 6.36337C10.3513 6.36337 10.2298 6.41091 10.1348 6.50599C10.0397 6.60106 9.99216 6.72254 9.99216 6.87044V9.49267H7.36992C7.22202 9.49267 7.10054 9.54021 7.00547 9.63529C6.91039 9.73036 6.86285 9.85184 6.86285 9.99974C6.86285 10.1476 6.91039 10.2691 7.00547 10.3642C7.10054 10.4593 7.22202 10.5068 7.36992 10.5068Z"
                                                                fill="black"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                    {isSingle && renderBtns()}
                </div>
            </div>
            {!isSingle && renderBtns()}
        </div>
    );
}
